import { useEffect, useState, useRef, useCallback } from 'react';
const SCROLL_LOAD_THRESHOLD = 0.5;
const BOTTOM_THRESHOLD = 5;
export function useInfiniteScroll({
  containerRef,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  items,
  resetKey
}) {
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [hasNewItems, setHasNewItems] = useState(false);
  const [isInTopHalf, setIsInTopHalf] = useState(false);
  const lastSeenItemIdRef = useRef(null);
  const hasScrolledToBottomOnLoadRef = useRef(false);
  const scrollToBottom = useCallback(({
    smooth
  }) => {
    setHasNewItems(false);
    const container = containerRef.current;
    if (!container) return;
    if (smooth) {
      container.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    } else {
      container.scrollTop = 0;
    }
  }, [containerRef]);

  // Fetch next available page when in top half of container
  useEffect(() => {
    if (isInTopHalf && !isFetchingNextPage && hasNextPage) {
      fetchNextPage();
    }
  }, [isInTopHalf, isFetchingNextPage, hasNextPage, fetchNextPage]);

  // Stay at bottom when new items added (with column-reverse, we're already at scrollTop=0)
  useEffect(() => {
    if (!items.length) return;
    const currentLastItemId = items[items.length - 1].id;
    const previousLastItemId = lastSeenItemIdRef.current;
    if (previousLastItemId && currentLastItemId !== previousLastItemId) {
      if (!isAtBottom) {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setHasNewItems(true);
      }
    }
    lastSeenItemIdRef.current = currentLastItemId;
  }, [items, isAtBottom, scrollToBottom]);

  // Set up scroll event listener
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const onScroll = () => {
      const scrollable = container.scrollHeight - container.clientHeight;
      const scrollTop = container.scrollTop;
      setIsAtBottom(scrollTop > -BOTTOM_THRESHOLD);
      const threshold = scrollable * SCROLL_LOAD_THRESHOLD;
      const inTopHalf = scrollTop <= -threshold;
      setIsInTopHalf(inTopHalf);
    };
    container.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => container.removeEventListener('scroll', onScroll);
  }, [containerRef]);
  useEffect(() => {
    hasScrolledToBottomOnLoadRef.current = false;
    lastSeenItemIdRef.current = null;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsAtBottom(true);
    setIsInTopHalf(false);
    scrollToBottom({
      smooth: false
    });
  }, [resetKey, scrollToBottom]);

  // Snap to bottom on initial load (with column-reverse, scrollTop=0 is bottom)
  useEffect(() => {
    const container = containerRef.current;
    if (!container || hasScrolledToBottomOnLoadRef.current) return;
    if (items.length === 0) return;
    container.scrollTop = 0;
    lastSeenItemIdRef.current = items[items.length - 1].id;
    hasScrolledToBottomOnLoadRef.current = true;
  }, [items.length, containerRef, items]);
  return {
    scrollToBottom,
    hasNewItems
  };
}